import React from 'react';
import { Button } from '@/components/ui/button';
import { Star, ExternalLink, Globe, Facebook, Youtube, Instagram } from 'lucide-react';
import { Display } from '@/types/sequence';
import AvatarUpload from './AvatarUpload';
import { MessageButton } from '@/components/chat';

interface SocialLinks {
  tiktok?: string;
  facebook?: string;
  website?: string;
  youtube?: string;
  instagram?: string;
}

interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  display_name: string;
  bio?: string;
  avatar_url?: string;
  is_public: boolean;
  social_links?: SocialLinks;
  created_at: string;
  updated_at: string;
}

interface UserProfileHeaderProps {
  user: UserProfile;
  userDisplay: Display | null;
  onEditProfile: () => void;
  isPublicView?: boolean;
}

const UserProfileHeader: React.FC<UserProfileHeaderProps> = ({
  user,
  userDisplay,
  onEditProfile,
  isPublicView = false
}) => {
  const handleAvatarUpdated = (url: string) => {
    // The avatar update is handled in the AvatarUpload component
    // We don't need to do anything here as the user metadata is updated automatically
  };

  const handleViewPublicProfile = () => {
    // Generate public profile URL
    const username = user?.username ||
                    user?.display_name?.toLowerCase().replace(/\s+/g, '') ||
                    'user';

    const publicUrl = `/profile/${username}`;
    window.open(publicUrl, '_blank');
  };

  // TikTok icon component
  const TikTokIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  );

  // Render social media links if they exist
  const renderSocialLinks = () => {
    const socialLinks = user?.social_links;
    if (!socialLinks) return null;

    const links = [
      { key: 'website', url: socialLinks.website, Icon: Globe, label: 'Website' },
      { key: 'facebook', url: socialLinks.facebook, Icon: Facebook, label: 'Facebook' },
      { key: 'youtube', url: socialLinks.youtube, Icon: Youtube, label: 'YouTube' },
      { key: 'instagram', url: socialLinks.instagram, Icon: Instagram, label: 'Instagram' },
      { key: 'tiktok', url: socialLinks.tiktok, Icon: TikTokIcon, label: 'TikTok' }
    ];

    const availableLinks = links.filter(link => link.url && link.url.trim() !== '');

    if (availableLinks.length === 0) return null;

    return (
      <div className="flex items-center gap-2 mt-2">
        {availableLinks.map(({ key, url, Icon, label }) => (
          <a
            key={key}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={label}
            title={label}
          >
            <Icon />
          </a>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-background rounded-lg shadow-xs border p-6">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="shrink-0">
          {!isPublicView ? (
            <AvatarUpload 
              currentAvatarUrl={user?.avatar_url}
              userId={user?.user_id}
              onAvatarUpdated={handleAvatarUpdated}
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-linear-to-r from-blue-500 to-purple-600 flex items-center justify-center">
              {user?.avatar_url ? (
                <img 
                  src={user.avatar_url} 
                  alt={user.display_name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <span className="text-white text-xl font-bold">
                  {user?.display_name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              )}
            </div>
          )}
        </div>
        
        <div className="grow">
          <h1 className="text-2xl font-bold text-foreground mb-1">
            {user?.display_name || 'User'}
          </h1>
          
          {user?.username && (
            <p className="text-base text-muted-foreground mb-2">@{user.username}</p>
          )}

          {user?.bio && (
            <p className="text-muted-foreground mb-3">{user.bio}</p>
          )}

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {userDisplay && (
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4" />
                <span>{userDisplay.location}</span>
              </div>
            )}
          </div>

          {renderSocialLinks()}
        </div>
        
        {!isPublicView ? (
          <div className="flex gap-3 mt-2 md:mt-0">
            <Button variant="outline" onClick={onEditProfile}>Edit Profile</Button>
            <Button
              variant="outline"
              onClick={handleViewPublicProfile}
              className="flex items-center gap-2"
            >
              <ExternalLink size={16} />
              View Public Profile
            </Button>
          </div>
        ) : (
          <div className="flex gap-3 mt-2 md:mt-0">
            <MessageButton
              userId={user.user_id}
              userName={user.display_name}
              variant="default"
              size="default"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileHeader;
